import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as React from "react"
import { useState } from "react"
import { getHttpHeaders } from "../../auth/selectors"
import { useSelector } from "react-redux"
import { NotificationType, openNotification } from "../Notification/Notification"
import { ImageUploadBase } from "./components/ImageUploadBase"
import { CropModal } from "./components/CropModal"
import {
    convertUploadedImageToFile,
    getCropVariantByType,
    isCroppedVariantReusable,
    requestFileUpload,
    sanitizeFileNames,
    UNSUPPORTED_BROWSER_ERROR_CODE,
    validateAndProcessImage,
} from "./utils"
import { UnsupportedBrowserModal } from "./components/UnsupportedBrowserModal"
import { logger } from "../../../utils/loggerWrap"
import { CropOptions, CroppedVariant, ImageOptions, ImageUploadData, ImageUploadType } from "./types"
import { ImageModal } from "./components/ImageModal"
import { ImageCropInputData, ImageCropResultData } from "./components/types"
import * as uuid from "uuid"
import { Logger } from "../../../server/logger"

export type ImageUploadProps = {
    className?: string
    type: ImageUploadType
    offerImageCrop?: boolean
    description?: string
    allowEditUploadedImages?: boolean // Allow to edit already uploaded images
    cropModalTitle?: string
    cropModalDesc?: string
    cropOptions?: CropOptions[]
    maxImages?: number
    maxFileSize?: number // Max file size in MB
    acceptedFormats?: (".gif" | ".jpeg" | ".jpg" | ".png" | ".bmp" | ".heic" | ".heif")[]
    imageOptions?: ImageOptions
    onUploadStart?: () => any
    onUploadEnd?: () => any
    value: ImageUploadData[]
    onChange: (value: ImageUploadData[]) => void
}

export const ImageUpload = ({
    maxImages = 1,
    maxFileSize = 5,
    allowEditUploadedImages = false,
    onUploadStart = () => {},
    onUploadEnd = () => {},
    ...props
}: ImageUploadProps) => {
    const [loadingImages, setLoadingImages] = useState<Maybe<string[]>>(null)
    const [progress, setProgress] = useState<number>(0)
    const [cropFiles, setCropFiles] = useState<Maybe<ImageCropInputData[]>>(null)
    const [sizeError, setSizeError] = useState(false)
    const [showBrowserWarnModal, setBrowserWarnModal] = useState(false)
    const [previewImg, setPreviewImage] = useState<Maybe<ImageUploadData>>(null)
    const [editImg, setEditImage] = useState<Maybe<ImageUploadData>>(null)

    const headers = useSelector(getHttpHeaders)

    const uploadCroppedImages = async (croppedImages: ImageCropResultData[]) => {
        try {
            const result: ImageUploadData[] = []

            for (const cropImage of croppedImages) {
                const cropVariantResult: CroppedVariant[] = []
                for (const cropVariant of cropImage.croppedVariants) {
                    const file = cropVariant.file
                    if (!file) {
                        continue
                    }

                    // If we are editing and already uploaded image, check whether cropping changed
                    if (editImg) {
                        const prevCropVariant = getCropVariantByType(editImg, cropVariant.cropType)

                        // If previous image exists and matches the new output (crop coords, format, dimensions),
                        // keep the previous URL (no need to re-upload)
                        if (prevCropVariant && (await isCroppedVariantReusable(prevCropVariant, cropVariant))) {
                            cropVariantResult.push({
                                url: prevCropVariant.url,
                                cropCoords: cropVariant.cropCoords,
                                cropType: cropVariant.cropType,
                            })
                            continue
                        }
                    }

                    cropVariantResult.push({
                        url: await requestFileUpload(file, headers, setProgress),
                        cropCoords: cropVariant.cropCoords,
                        cropType: cropVariant.cropType,
                    })
                }

                result.push({
                    originalImage: {
                        // If we are editing an already uploaded image, keep the original URL
                        url:
                            editImg?.originalImage.url ??
                            (await requestFileUpload(cropImage.file, headers, setProgress)),
                        id: editImg?.originalImage.id ?? uuid.v7(),
                    },
                    croppedVariants: cropVariantResult,
                })
            }

            props.onChange([
                ...props.value.filter(v => !editImg || v.originalImage.id !== editImg.originalImage.id),
                ...result,
            ])
        } catch (e) {
            onError(e)
        }
    }

    const uploadUncroppedImages = async (files: ImageCropInputData[]) => {
        try {
            const updatedData: ImageUploadData[] = []
            for (const file of files) {
                const url = await requestFileUpload(file.file, headers, setProgress)
                updatedData.push({ originalImage: { url: url, id: uuid.v7() } })
            }
            props.onChange([...props.value, ...updatedData])

            setLoadingImages(null)
            setProgress(0)
        } catch (e) {
            onError(e)
        }
    }

    const onDrop = async (origFiles: File[]) => {
        try {
            onUploadStart()

            // Sanitize file names
            const files = sanitizeFileNames(origFiles)

            setLoadingImages([...(loadingImages ?? []), ...files.map(f => f.name)])
            const processedFiles: ImageCropInputData[] = []
            for (const file of files) {
                // Process image: convert, downscale
                const processed = await validateAndProcessImage(file, props.imageOptions ?? null, maxFileSize, () =>
                    setSizeError(true)
                )

                processedFiles.push({
                    file: processed,
                })
            }

            if (props.offerImageCrop) {
                setCropFiles(processedFiles)
            } else {
                await uploadUncroppedImages(processedFiles)
                onUploadEnd()
            }
        } catch (e) {
            onError(e)
        }
    }

    const onCropFinished = async (croppedData: ImageCropResultData[]) => {
        try {
            setCropFiles(null)
            setSizeError(false)
            setLoadingImages(croppedData.map((_, idx) => `image-${idx}`))

            await uploadCroppedImages(croppedData)

            setLoadingImages(null)
            setEditImage(null)
            setProgress(0)
            onUploadEnd()
        } catch (e) {
            onError(e)
        }
    }

    const onCropCancel = () => {
        setCropFiles(null)
        setSizeError(false)
        setLoadingImages(null)
        setProgress(0)
        setEditImage(null)
        onUploadEnd()
    }

    const onEditLabel = (imgId: string, label: string) => {
        props.onChange(props.value.map(v => (v.originalImage.id === imgId ? { ...v, label: label } : v)))
    }

    const onRemove = (imgId: string) => {
        setLoadingImages(null)
        setSizeError(false)

        props.onChange(props.value.filter((data: ImageUploadData) => data.originalImage.id !== imgId))
    }

    const onError = (e: Error, message?: string) => {
        setLoadingImages(null)
        setPreviewImage(null)
        setEditImage(null)
        logger.error("ImageUpload - onError", null, e)

        if (isBrowserError(e)) {
            setBrowserWarnModal(true)
        } else {
            openNotification(
                NotificationType.Error,
                message || "Could not upload file",
                "Please try again, or try different browser."
            )
        }
        onUploadEnd()
    }

    const isBrowserError = e => e?.code === UNSUPPORTED_BROWSER_ERROR_CODE

    const openImagePreview = (imageData: Maybe<ImageUploadData>) => {
        setPreviewImage(imageData)
    }

    const openImageEdit = async (imageData: Maybe<ImageUploadData>) => {
        try {
            setEditImage(imageData)

            if (!imageData) {
                return
            }

            onUploadStart()

            const file = await convertUploadedImageToFile(imageData.originalImage.url)
            const croppedVariants = props.value.find(
                v => v.originalImage.id === imageData.originalImage.id
            )?.croppedVariants

            setCropFiles([{ file: file, croppedVariants: croppedVariants }])
        } catch (e) {
            Logger.error("ImageUpload - openImageEdit - error", null, e)
            onError(e, "Could not open image for editing")
        }
    }

    return (
        <>
            <ImageUploadBase
                className={props.className}
                onDrop={onDrop}
                loadingImages={loadingImages}
                onRemove={onRemove}
                progress={progress}
                images={props.value}
                type={props.type}
                maxImages={maxImages}
                openImagePreview={openImagePreview}
                openImageEdit={openImageEdit}
                allowEditUploadedImages={allowEditUploadedImages}
                imageOptions={props.imageOptions}
                description={props.description}
                onEditLabel={onEditLabel}
                onChangeImages={props.onChange}
            />
            {cropFiles ? (
                <CropModal
                    imagesToCrop={cropFiles}
                    title={props.cropModalTitle}
                    description={props.cropModalDesc}
                    cropOptions={props.cropOptions}
                    imageOptions={props.imageOptions}
                    showSizeWarning={sizeError}
                    onConfirm={onCropFinished}
                    onCancel={onCropCancel}
                />
            ) : null}
            {previewImg ? (
                <ImageModal
                    src={previewImg.originalImage.url}
                    isOpen={true}
                    onClose={() => {
                        setPreviewImage(null)
                    }}
                    onRemove={() => {
                        setPreviewImage(null)
                        onRemove(previewImg?.originalImage.id)
                    }}
                />
            ) : null}
            {showBrowserWarnModal ? <UnsupportedBrowserModal onClose={() => setBrowserWarnModal(false)} /> : null}
        </>
    )
}
